import type { AllocationWindow, AllocationWindowInput, WindowApiError } from '../../../api/organization/allocationWindowTypes';

/**
 * The window editor's draft (ADR-0027, M4 spec §6) and the calendar-day
 * helpers the allocations tab needs. Pure, so the rules are tested without a
 * screen. The server owns every rule (ADR-0020): these only keep an
 * obviously incomplete row from being sent, and say which row it is.
 */

export interface WindowDraftRow extends AllocationWindowInput {
    /** Local key for React; the stored id is not sent back (the server matches by content). */
    key: string;
}

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const isTime = (value: string): boolean => TIME.test(value);
export const isEndTime = (value: string): boolean => value === '24:00' || TIME.test(value);

/** A real calendar day, `YYYY-MM-DD` (Feb 30 is not one). */
export const isDay = (value: string): boolean => {
    if (!DAY.test(value)) return false;
    const [y, m, d] = value.split('-').map(Number);
    const civil = new Date(Date.UTC(y, m - 1, d));
    return civil.getUTCFullYear() === y && civil.getUTCMonth() === m - 1 && civil.getUTCDate() === d;
};

export type RowProblem = 'weekday' | 'start' | 'end' | 'end_before_start' | 'valid_from' | 'valid_to' | 'valid_order';

/** What is wrong with a row, or null. Same checks the API's `window_invalid` makes. */
export const rowProblem = (row: AllocationWindowInput): RowProblem | null => {
    if (!WEEKDAYS.includes(row.weekday as typeof WEEKDAYS[number])) return 'weekday';
    if (!isTime(row.startTime)) return 'start';
    if (!isEndTime(row.endTime)) return 'end';
    if (row.endTime !== '24:00' && row.endTime <= row.startTime) return 'end_before_start';
    if (!isDay(row.validFrom)) return 'valid_from';
    if (!isDay(row.validTo)) return 'valid_to';
    if (row.validTo < row.validFrom) return 'valid_order';
    return null;
};

export const toDraft = (windows: AllocationWindow[]): WindowDraftRow[] =>
    windows.map((window) => ({
        key: window.id,
        weekday: window.weekday,
        startTime: window.startTime,
        endTime: window.endTime,
        validFrom: window.validFrom,
        validTo: window.validTo,
    }));

export const toInput = (rows: WindowDraftRow[]): AllocationWindowInput[] =>
    rows.map(({ weekday, startTime, endTime, validFrom, validTo }) => ({
        weekday, startTime, endTime, validFrom, validTo,
    }));

/** Dirty compares content, not the local keys. */
export const draftEquals = (a: WindowDraftRow[], b: WindowDraftRow[]): boolean =>
    JSON.stringify(toInput(a)) === JSON.stringify(toInput(b));

/** A new row: Monday evening, valid for the season the editor was given. */
export const newRow = (key: string, season: { from: string, to: string } | null): WindowDraftRow => ({
    key,
    weekday: 1,
    startTime: '18:00',
    endTime: '20:00',
    validFrom: season?.from ?? '',
    validTo: season?.to ?? '',
});

// ── Calendar days (civil dates, zone-free) ──────────────────────────────

const civil = (day: string): Date => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
};
const dayOf = (date: Date): string => {
    const y = String(date.getUTCFullYear()).padStart(4, '0');
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/** ISO weekday of a calendar day: 1 = Monday … 7 = Sunday. */
export const weekdayOf = (day: string): number => {
    const js = civil(day).getUTCDay();
    return js === 0 ? 7 : js;
};

/**
 * The window's dates from `fromDay` on (inclusive), at most `limit`, inside
 * its validity — the dates a release can name. Whether one has STARTED is the
 * server's to say (`release_started`), since the start is a time on the
 * room's clock.
 */
export const occurrenceDays = (
    window: Pick<AllocationWindowInput, 'weekday' | 'validFrom' | 'validTo'>,
    fromDay: string,
    limit: number,
): string[] => {
    const first = fromDay > window.validFrom ? fromDay : window.validFrom;
    if (!isDay(first) || !isDay(window.validTo) || first > window.validTo) return [];
    const cursor = civil(first);
    cursor.setUTCDate(cursor.getUTCDate() + ((window.weekday - weekdayOf(first) + 7) % 7));
    const days: string[] = [];
    while (days.length < limit) {
        const day = dayOf(cursor);
        if (day > window.validTo) break;
        days.push(day);
        cursor.setUTCDate(cursor.getUTCDate() + 7);
    }
    return days;
};

/**
 * The message for a failed window call: the server's own sentence where it
 * sent one (it names the rule), a generic one otherwise. Never a raw code.
 */
export const windowErrorMessage = (error: unknown, fallback: string): string => {
    const body = (error as { data?: WindowApiError } | undefined)?.data;
    return body?.message?.trim() || fallback;
};

export const windowErrorCode = (error: unknown): string | undefined =>
    (error as { data?: WindowApiError } | undefined)?.data?.code;

/** A calendar day as the admin writes dates elsewhere (`formatDate`), without a clock: 29.10.2026 / 29/10/2026. */
export const formatDay = (day: string, language: 'de' | 'en'): string => {
    if (!isDay(day)) return day;
    const [y, m, d] = day.split('-');
    return language === 'de' ? `${d}.${m}.${y}` : `${d}/${m}/${y}`;
};
